import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from './api';

interface User {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  tenantId: string;
  roles: string[];
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'rf3_access_token';
const REFRESH_TOKEN_KEY = 'rf3_refresh_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserInfo = useCallback(async (token: string) => {
    try {
      api.setAccessToken(token);
      const response = await fetch('/oauth/userinfo', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user info');
      }

      const userInfo = await response.json();
      setUser({
        id: userInfo.sub,
        email: userInfo.email,
        displayName: userInfo.name || null,
        avatarUrl: userInfo.picture || null,
        tenantId: userInfo.tenant_id,
        roles: userInfo.roles || [],
        permissions: userInfo.permissions || [],
      });
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetchUserInfo(token).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [fetchUserInfo]);

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code) {
      // Exchange code for tokens
      const exchangeCode = async () => {
        try {
          const codeVerifier = sessionStorage.getItem('code_verifier');
          const tokenBody: Record<string, string> = {
            grant_type: 'authorization_code',
            code,
            redirect_uri: `${window.location.origin}/callback`,
            client_id: 'hub-frontend',
          };
          if (codeVerifier) {
            tokenBody.code_verifier = codeVerifier;
          }

          const response = await fetch('/oauth/token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(tokenBody),
          });

          if (!response.ok) {
            throw new Error('Token exchange failed');
          }

          const tokens = await response.json();
          localStorage.setItem(TOKEN_KEY, tokens.access_token);
          if (tokens.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
          }

          // Verifier is single-use; clear after redeem.
          sessionStorage.removeItem('code_verifier');
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_nonce');

          await fetchUserInfo(tokens.access_token);

          // Clean up URL
          window.history.replaceState({}, '', '/');
        } catch (error) {
          console.error('Token exchange failed:', error);
        } finally {
          setIsLoading(false);
        }
      };

      exchangeCode();
    }
  }, [fetchUserInfo]);

  const login = useCallback(async () => {
    // Generate PKCE code verifier and challenge (RFC 7636, S256).
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallengeS256(codeVerifier);

    // Store code verifier for later use
    sessionStorage.setItem('code_verifier', codeVerifier);

    const state = generateRandomString(32);
    const nonce = generateRandomString(32);

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_nonce', nonce);

    const params = new URLSearchParams({
      client_id: 'hub-frontend',
      redirect_uri: `${window.location.origin}/callback`,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `/oauth/authorize?${params.toString()}`;
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    api.setAccessToken(null);
    setUser(null);

    // Call logout endpoint
    window.location.href = `/oauth/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
  }, []);

  const hasPermission = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) => permissions.some((p) => user?.permissions.includes(p)),
    [user]
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]) => permissions.every((p) => user?.permissions.includes(p)),
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// PKCE helpers (RFC 7636)
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateCodeVerifier(): string {
  // 32 random bytes → 43 base64url chars (within RFC 7636's 43–128 range).
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function generateCodeChallengeS256(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}
